import { CustomError } from '@kulee/helper'
import { RESPONSE_CODES } from '@kulee/tga-constant'
import { Md5 } from 'ts-md5'
import BaseService from '../../BaseService'

import type { CustomAxiosResponse, PageParams } from '../../types'
import type {
  AvatarItem,
  BindUserEmailOptions,
  BindUserPhoneOptions,
  CoverItem,
  LoginOptions,
  RegisterOptions,
  SendEmailCodeOptions,
  Token,
  UpdateUserExtInfoOptions,
  UpdateUserInfoOptions,
  UpdateUserPasswordOptions,
  UserInfo
} from '../../types/uc'

class UCService extends BaseService {
  /**
   * 验证帐号是否存在
   * @param account 用户帐号
   * @returns 是否存在
   */
  public async checkAccountExist(account: string): Promise<CustomAxiosResponse<boolean>> {
    return this.request.get<unknown, CustomAxiosResponse<boolean>>(`${this.apiUrl}/v1/user-center/account/exist`, {
      params: {
        account
      }
    })
  }

  /**
   * 用户注册
   * @param options 参数
   * @param options.account 账号
   * @param options.email 邮箱
   * @param options.phone 手机号
   * @param options.password 密码
   * @param options.code 验证码
   * @param options.uuid 唯一标识符
   * @param options.inviteCode 邀请码
   * @returns 用户Token
   */
  public async register(options: RegisterOptions): Promise<CustomAxiosResponse<Token>> {
    const { password, ...rest } = options

    return this.request.post<unknown, CustomAxiosResponse<Token>>(`${this.apiUrl}/v1/user-center/register`, {
      ...rest,
      password: password ? Md5.hashStr(password) : undefined
    })
  }

  /**
   * 登录
   * @param options 参数
   * @param options.account 账号
   * @param options.email 邮箱
   * @param options.phone 手机号
   * @param options.password 密码
   * @param options.code 验证码
   * @param options.uuid 唯一标识符
   * @returns 用户Token
   */
  public async login(options: LoginOptions): Promise<CustomAxiosResponse<Token>> {
    const { password, ...rest } = options

    return this.request.post<unknown, CustomAxiosResponse<Token>>(`${this.apiUrl}/v1/user-center/login`, {
      ...rest,
      password: password ? Md5.hashStr(password) : undefined,
      timestamp: new Date().getTime()
    })
  }

  /**
   * 退出登录
   * @returns null
   */
  public async logout(): Promise<CustomAxiosResponse<null>> {
    return this.request.delete<unknown, CustomAxiosResponse<null>>(`${this.apiUrl}/v1/user-center/logout`)
  }

  /**
   * 发送邮箱验证码
   * @param options 参数
   * @param options.email 邮箱
   * @param options.grantType 授权类型
   * @returns null
   */
  public async sendEmailCode(options: SendEmailCodeOptions): Promise<CustomAxiosResponse<null>> {
    return this.request.get<unknown, CustomAxiosResponse<null>>(`${this.apiUrl}/v1/user-center/verify-code/email`, {
      params: options
    })
  }

  /**
   * 获取用户信息
   * @returns 用户信息
   */
  public async getUserInfo(): Promise<CustomAxiosResponse<UserInfo>> {
    return this.request.get<unknown, CustomAxiosResponse<UserInfo>>(`${this.apiUrl}/v1/user-center/app/user-info`)
  }

  /**
   * 更新用户信息
   * @param options 用户信息
   * @param options.nickname 昵称
   * @param options.avatar 头像
   * @param options.cover 封面
   * @returns null
   */
  public async updateUserInfo(options: UpdateUserInfoOptions): Promise<CustomAxiosResponse<null>> {
    return this.request.put<unknown, CustomAxiosResponse<null>>(`${this.apiUrl}/v1/user-center/app/user-info`, options)
  }

  /**
   * 更新用户扩展信息
   * @param options 参数
   * @param options.key 键
   * @param options.value 对象值
   * @param options.text 文本值
   * @returns null
   */
  public async updateUserExtInfo(options: UpdateUserExtInfoOptions): Promise<CustomAxiosResponse<null>> {
    const { value, text } = options
    if (typeof value === 'undefined' && typeof text === 'undefined') {
      throw new CustomError({
        code: RESPONSE_CODES.PARAMETER_INVALID,
        message: 'value or text is required'
      })
    }

    return this.request.put<unknown, CustomAxiosResponse<null>>(
      `${this.apiUrl}/v1/user-center/app/user-info/ext`,
      options
    )
  }

  /**
   * 绑定用户邮箱
   * @param options 参数
   * @param options.email 邮箱
   * @param options.code 验证码
   * @returns null
   */
  public async bindUserEmail(options: BindUserEmailOptions): Promise<CustomAxiosResponse<null>> {
    return this.request.post<unknown, CustomAxiosResponse<null>>(
      `${this.apiUrl}/v1/user-center/app/user-info/bind/email`,
      options
    )
  }

  /**
   * 绑定用户手机号
   * @param options 参数
   * @param options.phone 手机号
   * @param options.code 验证码
   * @returns null
   */
  public async bindUserPhone(options: BindUserPhoneOptions): Promise<CustomAxiosResponse<null>> {
    return this.request.post<unknown, CustomAxiosResponse<null>>(
      `${this.apiUrl}/v1/user-center/app/user-info/bind/phone`,
      options
    )
  }

  /**
   * 修改密码
   * @param options 参数
   * @param options.newPassword 新密码
   * @param options.oldPassword 旧密码
   * @param options.code 验证码
   * @param options.type 类型 0: 原密码修改 1: 邮箱修改 2: 手机号修改
   * @returns null
   */
  public async updatUserPassword(options: UpdateUserPasswordOptions): Promise<CustomAxiosResponse<null>> {
    const { newPassword, oldPassword, code, type } = options

    if (type === 0 && !oldPassword) {
      throw new CustomError({
        code: RESPONSE_CODES.PARAMETER_INVALID,
        message: 'oldPassword is required'
      })
    } else if ((type === 1 || type === 2) && !code) {
      throw new CustomError({
        code: RESPONSE_CODES.PARAMETER_INVALID,
        message: 'code is required'
      })
    }

    return this.request.put<unknown, CustomAxiosResponse<null>>(
      `${this.apiUrl}/v1/user-center/app/user-info/password`,
      {
        newPassword: Md5.hashStr(newPassword),
        oldPassword: oldPassword ? Md5.hashStr(oldPassword) : undefined,
        code,
        type
      }
    )
  }

  /**
   * 重黑密码（忘记密码）
   * @param options 参数
   * @param options.email 邮箱
   * @param options.code 验证码
   * @param options.newPassword 新密码
   * @returns null
   */
  public async resetUserPassword(options: {
    email: string
    code: string
    newPassword: string
  }): Promise<CustomAxiosResponse<null>> {
    return this.request.post<unknown, CustomAxiosResponse<null>>(
      `${this.apiUrl}/v1/user-center/app/user-info/password/reset`,
      {
        ...options,
        newPassword: Md5.hashStr(options.newPassword)
      }
    )
  }

  /**
   * 获取系统头像配置列表
   * @param options 参数
   * @param options.pageNum 页码
   * @param options.pageSize 页大小
   * @param options.orderBy 排序字段
   * @returns 系统头像配置列表
   */
  public async getAvatarConfigList(options: PageParams = {}): Promise<CustomAxiosResponse<AvatarItem[]>> {
    return this.request.get<unknown, CustomAxiosResponse<AvatarItem[]>>(
      `${this.apiUrl}/v1/user-center/app/sys/avatar`,
      {
        params: options
      }
    )
  }

  /**
   * 获取系统封面配置列表
   * @param options 参数
   * @param options.pageNum 页码
   * @param options.pageSize 页大小
   * @param options.orderBy 排序字段
   * @returns 系统封面配置列表
   */
  public async getCoverConfigList(options: PageParams = {}): Promise<CustomAxiosResponse<CoverItem[]>> {
    return this.request.get<unknown, CustomAxiosResponse<CoverItem[]>>(`${this.apiUrl}/v1/user-center/app/sys/cover`, {
      params: options
    })
  }
}

export default UCService
